import { createContext, useContext } from 'react';
import { readLayoutObject, storage, writeLayoutMember } from './layoutStore';

/** The user's choice (spec 6.1.9). */
export type ThemePreference = 'system' | 'light' | 'dark';
/** What is shown. */
export type EffectiveTheme = 'light' | 'dark';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

/** Last theme used in this browser, read by `public/theme-init.js` before the app loads. */
export const THEME_MIRROR_KEY = 'nebisys.theme';

export { layoutKey } from './layoutStore';

export function parsePreference(value: unknown): ThemePreference | null {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value)
    ? (value as ThemePreference)
    : null;
}

/** The user's stored preference, else the browser's last one, else System. Never throws. */
export function readPreference(user: string | null): ThemePreference {
  const store = storage();
  if (!store) return 'system';
  try {
    const own = user ? parsePreference(readLayoutObject(user, store).theme) : null;
    return own ?? parsePreference(store.getItem(THEME_MIRROR_KEY)) ?? 'system';
  } catch {
    return 'system';
  }
}

/** Stores the preference in the user's layout object (keeping its other members) and the mirror. */
export function writePreference(user: string | null, preference: ThemePreference): void {
  const store = storage();
  if (!store) return;
  try {
    if (user) writeLayoutMember(user, 'theme', preference, store);
    store.setItem(THEME_MIRROR_KEY, preference);
  } catch {
    // Quota or privacy settings: the choice lasts for this page only.
  }
}

export const DARK_QUERY = '(prefers-color-scheme: dark)';

export function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function effectiveTheme(preference: ThemePreference, prefersDark: boolean): EffectiveTheme {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

export function applyTheme(theme: EffectiveTheme, root: HTMLElement = document.documentElement): void {
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
}

export interface ThemeState {
  preference: ThemePreference;
  effective: EffectiveTheme;
  setPreference: (preference: ThemePreference) => void;
}

export const ThemeContext = createContext<ThemeState>({
  preference: 'system',
  effective: 'light',
  setPreference: () => {},
});

export function useTheme(): ThemeState {
  return useContext(ThemeContext);
}
